import {EnvTypes, TypeImplementation, withSystem} from '@packages/systems/wfdl';
import {a, functionOf, TGeneric} from '@packages/systems/wfdl-ast';
import {TOption} from '@packages/systems/plugins/Option';
import {TDynamoItemRef} from '@packages/systems/plugins-utils/DynamoGateway';
import {
  toOperandExpr,
  TOperand,
} from '@packages/systems/plugins/ConditionsOperand';
import {
  BooleanTag,
  FALSE,
  toBooleanTag,
  TPredicate,
  TRUE,
} from '@packages/systems/plugins-utils/Boolean';
import type {DynamoEnvValueType} from '../types';
import {iconName, qualify} from '@packages/systems/wfdl-Qualified';
import {pluginName} from '../constants';
import {
  RefConditionValue,
  toListOfIds,
  TRefAnyOfConditionValue,
} from '../dataTypes/RefAnyOfConditionValue';

export const ItemRefType = TypeImplementation.Custom({
  params: ['referencedCollectionId'],
  render: () => null,
  icon: TypeImplementation.IconByName(qualify(pluginName, iconName('ItemRef'))),
});

export const dynamoItemRefCondition: EnvTypes.TypedValue = {
  envType: {
    dataType: TGeneric({
      arg: 'a',
      body: functionOf(
        TOption(TDynamoItemRef(a)),
        TOperand(TOption(TDynamoItemRef(a)))
      ),
    }),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  expression: toOperandExpr,
};

export const dynamoItemRefIsAny: EnvTypes.TypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'is any'},
      TGeneric({
        arg: 'a',
        body: functionOf(
          TRefAnyOfConditionValue(a),
          TPredicate(TOption(TDynamoItemRef(a)))
        ),
      })
    ),
    exported: 'visible',
  },
  value:
    (refValues: RefConditionValue[]) =>
    (x: DynamoEnvValueType<unknown>): BooleanTag => {
      if (x === null || x.tag === 'None') {
        return FALSE;
      }

      const itemRefVal = x.val;

      if (
        itemRefVal == null ||
        typeof itemRefVal !== 'object' ||
        !('id' in itemRefVal) ||
        typeof itemRefVal.id !== 'string'
      ) {
        return FALSE;
      }

      const refs = toListOfIds(refValues);
      return toBooleanTag(refs.includes(itemRefVal.id));
    },
};

export const dynamoItemRefIsNotAny: EnvTypes.TypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'is not any'},
      TGeneric({
        arg: 'a',
        body: functionOf(
          TRefAnyOfConditionValue(a),
          TPredicate(TOption(TDynamoItemRef(a)))
        ),
      })
    ),
    exported: 'visible',
  },
  value:
    (refValues: RefConditionValue[]) =>
    (x: DynamoEnvValueType<unknown>): BooleanTag => {
      if (x === null || x.tag === 'None') {
        return TRUE;
      }

      const itemRefVal = x.val;

      if (
        itemRefVal == null ||
        typeof itemRefVal !== 'object' ||
        !('id' in itemRefVal) ||
        typeof itemRefVal.id !== 'string'
      ) {
        return TRUE;
      }

      const refs = toListOfIds(refValues);
      return toBooleanTag(!refs.includes(itemRefVal.id));
    },
};

export const dynamoItemRefIsSet: EnvTypes.TypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'is set'},
      TGeneric({
        arg: 'a',
        body: TPredicate(TOption(TDynamoItemRef(a))),
      })
    ),
    exported: 'visible',
  },
  value: (x: DynamoEnvValueType<unknown>): BooleanTag => {
    if (x === null || x.tag === 'None') {
      return FALSE;
    }
    return TRUE;
  },
};

export const dynamoItemRefIsNotSet: EnvTypes.TypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'is not set'},
      TGeneric({
        arg: 'a',
        body: TPredicate(TOption(TDynamoItemRef(a))),
      })
    ),
    exported: 'visible',
  },
  value: (x: DynamoEnvValueType<unknown>): BooleanTag => {
    if (x === null || x.tag === 'None') {
      return TRUE;
    }
    return FALSE;
  },
};
